import { Button, Separator } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useTranslation } from '@web/src/lib/intl/react'
import { XIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { usePwaInstall } from 'src/hooks/usePwaInstall'
import Cookies from 'universal-cookie'

import { useAuth } from '../../hooks/use-auth'
import PwaInstallButton from './pwa-install-button'

const PwaInstallBar = () => {
  const { t } = useTranslation()
  const { isAuthenticated, currentUser } = useAuth()
  const { isInstallable, isInstalled, isIOS } = usePwaInstall()
  const [showInstallBar, setShowInstallBar] = useState(false)
  const [showFlare, setShowFlare] = useState(false)
  const flareAnimationRef = useRef<HTMLDivElement | null>(null)

  const cookies = new Cookies()
  const canShowBar =
    (isInstallable || isIOS) && isAuthenticated && !isInstalled && Boolean(currentUser?.profile?.signUpCompleted)

  useEffect(() => {
    if (!canShowBar) {
      setShowInstallBar(false)
      setShowFlare(false)
      return
    }

    if (new Cookies().get('dismissAppInstallBar')) {
      setShowInstallBar(false)
      return
    }

    // Wait until the bar is allowed to show, then slide it in. Starting this timer on
    // mount lets it finish during welcome, so the bar appears instantly on /feed.
    const showBarTimer = setTimeout(() => {
      setShowInstallBar(true)
    }, 3000)

    return () => clearTimeout(showBarTimer)
  }, [canShowBar])

  useEffect(() => {
    if (!showInstallBar) {
      return
    }

    const flareTimer = setTimeout(() => {
      setShowFlare(true)
    }, 300)

    return () => clearTimeout(flareTimer)
  }, [showInstallBar])

  // Reset and replay flare animation when it completes
  useEffect(() => {
    if (showFlare && flareAnimationRef.current) {
      const handleAnimationEnd = () => {
        setShowFlare(false)
        setTimeout(() => setShowFlare(true), 5000) // Replay every 5 seconds
      }

      const flareAnimation = flareAnimationRef.current
      flareAnimation.addEventListener('animationend', handleAnimationEnd)

      return () => {
        if (flareAnimation) {
          flareAnimation.removeEventListener('animationend', handleAnimationEnd)
        }
      }
    }
  }, [showFlare])

  const handleDismiss = () => {
    cookies.set('dismissAppInstallBar', 'true', { path: '/' })
    setShowInstallBar(false)
  }

  if (!(isInstallable || isIOS)) {
    return null
  }

  if (isInstalled || !isAuthenticated || !currentUser?.profile?.signUpCompleted) {
    return null
  }

  return (
    <div
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center transition-transform duration-500 ease-in-out',
        showInstallBar ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      <div className="pointer-events-auto relative flex w-full items-center gap-2 overflow-hidden bg-red-950 p-2 md:w-auto">
        {showFlare && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 z-1 h-full w-[150px] animate-[sf-pwa-install-flare_2s_ease-in-out] bg-[linear-gradient(90deg,rgba(255,255,255,0)_0%,rgba(255,255,255,0.6)_50%,rgba(255,255,255,0)_100%)] opacity-0"
            ref={flareAnimationRef}
          />
        )}

        <Button
          aria-label={t('Dismiss')}
          className="z-2 text-white hover:bg-white/10 hover:text-white"
          onClick={handleDismiss}
          size="icon-sm"
          variant="ghost"
        >
          <XIcon />
        </Button>

        <Separator className="bg-white/30" orientation="vertical" />

        <LogoFull />

        <div className="flex-1" />

        <PwaInstallButton
          className="z-2 mr-2"
          installClicked={() => {
            handleDismiss()
          }}
          size="default"
        />
      </div>
    </div>
  )
}

export default PwaInstallBar
